import React, {useEffect, useMemo, useState} from "react";
import {Button, Drawer, Input, message, Table, Tag} from "antd";
import type {ColumnsType} from "antd/es/table";
import {useMutation, useQuery} from "@apollo/client";
import {ReviewParcelDetailType} from "../graphql/types/ReviewParcelDetailType";
import CentralParcelDetailDrawer from "../../../central/parcels/components/CentralParcelDetailDrawer";
import {
    DeliveryReviewDetailResponse,
    GET_DELIVERY_TASK_REVIEW_DETAIL
} from "../graphql/queries/getDeliveryTaskReviewDetail";
import {ReviewParcelType} from "../graphql/types/ReviewParcelType";
import {DriverType} from "../../../../graphql/types/AreaPlanningType";
import {getUserInfo} from "../../../../utils/auth";
import {useWarehouseContext} from '../../../../contexts/WarehouseContext';
import {SiteType} from "graphql/types/SiteType";
import { useTranslation } from 'react-i18next';
import {BATCH_MARK_SUSPECTED_MISSING} from "../../../../graphql/mutations/batchMarkSuspectedMissing";
import {formatTimestampToDateTime} from "../../../../utils/dateTimeUtils";


interface DepartureReviewDrawerProps {
    visible: boolean;
    title: string;
    record?: ReviewParcelType;
    onClose: () => void;
    onDataRefresh: () => void;
    allDrivers: DriverType[];
}

const DepartureReviewDrawer: React.FC<DepartureReviewDrawerProps> = ({
    visible,
    title,
    record,
    onClose,
    onDataRefresh,
    allDrivers
}) => {
    const { t } = useTranslation();
    const formatMisScanTime = (timestamp?: number | string | null) => {
        if (!timestamp) return "-";
        return formatTimestampToDateTime(timestamp).replace(/\//g, "-");
    };
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedParcels, setSelectedParcels] = useState<ReviewParcelDetailType[]>([]);
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
    const [parcelDrawerVisible, setParcelDrawerVisible] = useState(false);
    const [parcels,setParcels] = useState<ReviewParcelDetailType[]>();
    const [searchText, setSearchText] = useState<string>('');
    const {data,loading} = useQuery<DeliveryReviewDetailResponse>(GET_DELIVERY_TASK_REVIEW_DETAIL,{
        variables: {
            filter:{
                accountId: record?.accountId || ''
            }
        },
        skip: !record?.accountId || !visible
    })
    // 获取仓库信息进行数据隔离
    const { currentWarehouse } = useWarehouseContext();
    const siteType = currentWarehouse?.siteType;
  

    useEffect(() => {
        if (data?.getDeliveryTaskReviewDetail){
            const detailParcels = data?.getDeliveryTaskReviewDetail
                .map(detail => {
                    const driver = allDrivers.find(driver => driver.accountId === record?.accountId);
                    return {
                        id: detail.orderId,
                        area: detail.routeName,
                        trackingNumber: detail.trackingNumber,
                        driverName: driver?.name || '',
                        phone: driver?.phone || '',
                        isTransferIn: detail.isTransferIn,
                        isMisScannedByOtherDriver: detail.isMisScannedByOtherDriver,
                        misScannedDriverName: detail.misScannedDriverName,
                        misScannedDriverPhone: detail.misScannedDriverPhone,
                        misScanTime: detail.misScanTime,
                        misScanMarkedType: detail.misScanMarkedType,
                    };
                })
                .sort((a, b) => {
                    // 首先按 routeName 排序
                    const routeNameComparison = (a.area || '').localeCompare(b.area || '');
                    if (routeNameComparison !== 0) {
                        return routeNameComparison;
                    }
                    // 如果 routeName 相同，再按 trackingNumber 排序
                    return (a.trackingNumber || '').localeCompare(b.trackingNumber || '');
                });
            setParcels(detailParcels || []);
        }
    }, [data, allDrivers, record?.accountId]);

    // Batch Mark as Exception Mutation
    const [batchMarkSuspectedMissingMutation, {loading: batchMarkAsExceptionLoading}] = useMutation(
        BATCH_MARK_SUSPECTED_MISSING,
        {
            onCompleted: (data) => {
                if (data.batchMarkSuspectedMissing) {
                    // 操作成功，刷新主页面数据
                    onDataRefresh();
                } else {
                    message.error('Failed to mark as suspected missing');
                }
            },
            onError: (error) => {
                message.error(`${t('wms.review.messages.failedToMarkAsSuspectedMissing')}: ${error.message}`);
            }
        }
    );

    // 处理行选择
    const handleRowSelectionChange = (selectedKeys: React.Key[], selectedRows: ReviewParcelDetailType[]) => {
        setSelectedRowKeys(selectedKeys);
        setSelectedParcels(selectedRows);
    };

    // 处理Suspected Missing操作
    const handleSuspectedMissing = async () => {
        if (selectedParcels.length === 0) {
            message.warning(t('wms.review.messages.pleaseSelectAtLeastOneParcel'));
            return;
        }

        try {
            // 批量标记选中的包裹为suspected missing
            const orderIds = selectedParcels.map(parcel => parcel.id.toString());

            const result = await batchMarkSuspectedMissingMutation({
                variables: {
                    orderIds: orderIds,
                    exceptionHubId: siteType == SiteType.HUB ? currentWarehouse?.id! : currentWarehouse?.parentSiteId!,
                    exceptionWarehouseId: siteType == SiteType.WAREHOUSE ? currentWarehouse?.id! : undefined!
                }
            });

            if (result.data?.batchMarkSuspectedMissing) {
                message.success(t('wms.review.messages.successfullyMarkedParcelsAsSuspectedMissing', { count: selectedParcels.length }));
                onClose();
            }
        } catch (error) {
            console.error('Suspected missing error:', error);
        } finally {
            setSelectedRowKeys([]);
            setSelectedParcels([]);
        }
    };

    // 过滤后的包裹数据
    const filteredParcels = useMemo(() => {
        if (!parcels) return [];
        if (!searchText.trim()) return parcels;
        
        return parcels.filter(parcel => 
            parcel.trackingNumber?.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [parcels, searchText]);

    // 表格列定义
    const columns: ColumnsType<ReviewParcelDetailType> = [
        {
            title: t('wms.review.columns.area'),
            dataIndex: "area",
            width: 112,
        },
        {
            title: t('wms.review.columns.parcel'),
            dataIndex: "trackingNumber",
            width: 420,
            render: (text: string, record) => (
                <span style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
                    <span
                        style={{ color: '#9254de', cursor: 'pointer' }}
                        onClick={() => {
                            setSelectedOrderId(record.id);
                            setParcelDrawerVisible(true);
                        }}
                    >
                        {text}
                    </span>
                    <span style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginLeft: 6 }}>
                        {record.isTransferIn && (
                            <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: 11, width: 'fit-content' }}>
                                {t('wms.review.tag.transferIn')}
                            </Tag>
                        )}
                        {record.isMisScannedByOtherDriver && (
                            <Tag color="red" style={{ marginInlineEnd: 0, fontSize: 11, width: 'fit-content' }}>
                                {t('wms.review.columns.scannedByMistake')}
                            </Tag>
                        )}
                    </span>
                </span>
            )
        },
        {
            title: t('wms.review.columns.misScanMarkedType'),
            dataIndex: 'misScanMarkedType',
            width: 180,
            render: (_: string, record) => {
                if (!record.isMisScannedByOtherDriver) {
                    return <span>-</span>;
                }
                if (record.misScanMarkedType === 'DRIVER_NO_RETURN') {
                    return <span>{t('wms.review.scanOtherDrawer.actions.driverNoReturn')}</span>;
                }
                if (record.misScanMarkedType === 'WAREHOUSE_CONFIRM_RECEIVE') {
                    return <span>{t('wms.review.scanOtherDrawer.actions.confirmReceive')}</span>;
                }
                return <span>-</span>;
            }
        },
        {
            title: t('wms.review.columns.driverName'),
            dataIndex: "driver",
            width: 168,
            render: (text: string, record) => (
                <div>
                    <span className={'font-bold'}>{record.driverName || t('wms.review.unknown')}</span>
                    <span className={'text-xs text-gray-500'}> +1 {record.phone || t('wms.review.na')}</span>
                </div>
            )
        },
        {
            title: t('wms.review.columns.wrongDriverScanned'),
            dataIndex: 'misScannedDriverName',
            width: 328,
            onCell: () => ({
                style: {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    verticalAlign: 'top'
                }
            }),
            render: (_: string, record) => {
                if (!record.isMisScannedByOtherDriver) {
                    return <span>-</span>;
                }
                return (
                    <div>
                        <div
                            style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}
                        >
                            <span className={'font-bold'}>{record.misScannedDriverName || t('wms.review.unknown')}</span>
                            <span className={'text-xs text-gray-500'}> +1 {record.misScannedDriverPhone || t('wms.review.na')}</span>
                        </div>
                        <div className={'text-xs text-gray-500'}>
                            {t('wms.review.columns.scanTime')}: {formatMisScanTime(record.misScanTime)}
                        </div>
                    </div>
                );
            }
        },
    ];

    return (
        <Drawer
            title={title} // Restore original title prop
            width={1200}
            open={visible}
            onClose={onClose}
            extra={null}
            bodyStyle={{
                padding: '16px',
                height: '100vh',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <div className="flex justify-between items-center flex-shrink-0">
                <Input.Search
                    placeholder={t('wms.review.placeholders.trackingNumber') as string}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                    allowClear
                />
                <Button
                    type="primary"
                    onClick={handleSuspectedMissing}
                    loading={batchMarkAsExceptionLoading}
                    disabled={selectedRowKeys.length === 0}
                    style={{ backgroundColor: '#8A2BE2', borderColor: '#8A2BE2', color: 'white' }}
                >
                    {t('wms.review.buttons.suspectedMissing')}
                </Button>
            </div>

            <div className="flex-1 overflow-hidden">
                <Table
                    dataSource={filteredParcels}
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 1240, y: 'calc(100vh - 200px)' }}
                    size="small"
                    loading={loading}
                    rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys,
                        onChange: handleRowSelectionChange,
                    }}
                 className="mb-4"/>
            </div>

            {selectedOrderId && (
                <CentralParcelDetailDrawer
                    showInterceptionExemptionAlert={true}
                    visible={parcelDrawerVisible}
                    onClose={() => setParcelDrawerVisible(false)}
                    orderId={selectedOrderId}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                />
            )}


        </Drawer>
    );
};

export default DepartureReviewDrawer;
